import { AddToCalendarButton } from "@web/emailCalendar/AddToCalendarButton";
import { AddTaskButton } from "@web/tasks/AddTaskButton";
import { SaveMailButton } from "@web/library/SaveMailButton";
// The email behind a card, in a side drawer (`o`). Reuses the Phase 0 thread reader pieces
// (MessageCard + sandboxed MailFrame) against GET /cards/:id/thread, or GET /mail/threads/:key for
// a thread with no card (a task's or a reply-tracked thread). Not modal: j/k keep moving through
// cards (or tasks) and the drawer follows the selection. Reply, Reply all and Forward open the
// compose sheet on a draft the server starts; when a card exists, its own reply stays the main way.
import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Image as ImageIcon, X } from "lucide-react";
import type { MessageView } from "@shared/api";
import { participantsLabel } from "@web/lib/addresses";
import { useCardThread } from "@web/lib/feedQueries";
import { threadQuery } from "@web/lib/queries";
import { useNow } from "@web/lib/useNow";
import { MessageCard } from "@web/thread/MessageCard";
import { Button } from "@web/ui/Button";
import { Kbd } from "@web/ui/Kbd";
import { hasOthers, ReplyActions, useReplyCommands } from "@web/compose/ReplyActions";
import { screenTarget } from "@web/screener/screener";
import { SenderScreenLine } from "@web/screener/SenderScreenLine";
import { ThreadMemory } from "@web/remember/ThreadMemory";

function initiallyExpanded(messages: readonly MessageView[]): Set<string> {
  const keys = new Set(messages.filter((m) => m.isUnread).map((m) => m.key));
  const last = messages[messages.length - 1];
  if (last) keys.add(last.key);
  return keys;
}

function Messages({ messages, accountEmail }: { messages: MessageView[]; accountEmail: string }) {
  const [expanded, setExpanded] = useState(() => initiallyExpanded(messages));
  const now = useNow(60_000);
  const toggle = useCallback((key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  return (
    <div className="flex flex-col gap-2">
      {messages.map((m) => (
        <MessageCard
          key={m.key}
          message={m}
          accountEmail={accountEmail}
          expanded={expanded.has(m.key)}
          now={now}
          onToggle={toggle}
        />
      ))}
    </div>
  );
}

export function ThreadDrawer({
  cardId,
  threadKey,
  title,
  onClose,
}: {
  /** The card whose thread this is; null reads the thread directly by key. */
  cardId: string | null;
  threadKey: string | null;
  title: string;
  onClose: () => void;
}) {
  const [images, setImages] = useState(false);
  const cardThread = useCardThread(cardId, threadKey, images);
  const mailThread = useQuery({
    ...threadQuery(threadKey ?? "", images),
    enabled: !cardId && !!threadKey,
  });
  const thread = cardId ? cardThread : mailThread;
  const data = thread.data?.thread.key === threadKey || !threadKey ? thread.data : undefined;
  const messages = data?.messages ?? [];
  const hasHtml = messages.some((m) => m.bodyHtml);
  const sender = screenTarget(data);
  // A card registers these for its own thread; a task's or a waiting thread's drawer does here.
  useReplyCommands(!cardId && messages.length ? (threadKey ?? null) : null);

  return (
    <aside
      aria-label="Email thread"
      className="fixed inset-0 z-40 flex flex-col border-l border-line bg-bg shadow-soft md:inset-y-0 md:right-0 md:left-auto md:w-[min(720px,60vw)]"
    >
      <header className="flex flex-wrap items-start gap-3 border-b border-line bg-paper px-4 py-3">
        <div className="min-w-0 flex-1">
          <p className="mm-kicker">Email thread</p>
          <h2 className="truncate text-lg">{data?.thread.subject?.trim() || title}</h2>
          {data ? (
            <p className="truncate text-sm text-muted">
              {participantsLabel(data.thread.participants, data.thread.accountEmail, 4)}
              {data.thread.messageCount > 1 ? ` · ${data.thread.messageCount} messages` : ""}
            </p>
          ) : null}
        </div>
        {threadKey ? <SaveMailButton threadKey={threadKey} /> : null}
        {threadKey ? (
          <AddTaskButton threadKey={threadKey} title={data?.thread.subject ?? title} />
        ) : null}
        {threadKey ? <AddToCalendarButton threadKey={threadKey} /> : null}
        {hasHtml && !images ? (
          <Button size="sm" variant="ghost" onClick={() => setImages(true)}>
            <ImageIcon aria-hidden className="size-4" />
            <span className="hidden sm:inline">Load images</span>
          </Button>
        ) : null}
        {data && messages.length ? (
          <ReplyActions
            threadKey={data.thread.key}
            replyAll={hasOthers(messages, data.thread.accountEmail)}
            compact
            keys={false}
          />
        ) : null}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close thread"
          className="inline-flex items-center gap-1.5 rounded-md p-1.5 text-muted hover:bg-sunk hover:text-ink"
        >
          <Kbd className="hidden sm:inline-flex">O</Kbd>
          <X className="size-4" />
        </button>
      </header>
      {sender ? (
        <div className="border-b border-line bg-paper px-4 py-1">
          <SenderScreenLine key={`${sender.accountId}:${sender.address}`} target={sender} />
        </div>
      ) : null}
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 md:px-4">
        {/* U6: a card shows its own note; a task's or a waiting thread's drawer shows it here. */}
        {!cardId ? <ThreadMemory threadKey={threadKey} className="mb-3" /> : null}
        {!threadKey ? (
          <p className="py-10 text-center text-muted">This isn’t tied to an email thread.</p>
        ) : thread.isError && !data ? (
          <div className="py-10 text-center">
            <p className="font-serif text-xl">Couldn’t open this thread</p>
            <p className="mt-1 text-muted">Your mail is safe in Gmail.</p>
            <Button className="mt-3" size="sm" onClick={() => void thread.refetch()}>
              Try again
            </Button>
          </div>
        ) : messages.length ? (
          <Messages
            key={`${cardId ?? threadKey}:${images}`}
            messages={messages}
            accountEmail={data?.thread.accountEmail ?? ""}
          />
        ) : (
          <div aria-hidden className="rounded-xl bg-paper p-5 ring-1 ring-line">
            <div className="mb-3 h-4 w-40 rounded bg-sunk" />
            <div className="h-3 w-full rounded bg-sunk" />
            <div className="mt-2 h-3 w-4/5 rounded bg-sunk" />
          </div>
        )}
      </div>
    </aside>
  );
}
